import React, { useMemo, useEffect } from "react";
import { useStore } from "../../../store/store";
import { useRequiredMsgs } from "../../../store/requiredMsgs";
import { useTheme } from "@mui/material/styles";
import fractionToInt from "../../../util/fractionToInt";
import {
  sliderScreenOptions,
  group6,
  group7,
  screenType,
  SliderScreenOption,
} from "./defaults";
import findScreenChoices from "./findScreenChoices";
import { ScreenTypeObj } from "./SliderScreenAccordian";

const slidingScreenConfigs = ["OA-XO", "OX-AO", "OX", "XO"];

interface UpdateObj {
  addScreen?: string;
  screenPocket?: string;
  screenLayout?: string;
}

function useSliderScreenSelector() {
  const theme = useTheme();

  const width = useStore((state) => state.quote.unitSize);
  const widthFraction = useStore((state) => state.quote.unitSizeFraction);

  const sliderOverallWidth = fractionToInt(width, widthFraction);

  const height = useStore((state) => state.quote.height);
  const heightFraction = useStore((state) => state.quote.heightFraction);

  const sliderOverallHeight = fractionToInt(height, heightFraction);

  const addToQuote = useStore((state) => state.addToQuote);

  const sliderP = useStore((state) => state.quote.sliderPocket);
  const isPocketing = sliderP === "With Pocket";

  const chosenConfig = useStore((state) => state.quote.exactConfig);

  const screenTypeChoice = useStore((state) => state.quote.screenType);

  const screen = useStore((state) => state.quote.addScreen);
  const screenLayout = useStore((state) => state.quote.screenLayout);
  const slidingScr = useStore((state) => state.quote.addSlidingScreen);

  const sillStyle = useStore((state) => state.quote.sillStyle);

  const stiles = useStore((state) => state.quote.stilesAndRails);
  const isSlimline =
    stiles && (stiles.includes("Slimline") || stiles.includes("Lock"));

  const screenVal = screen
    ? screen.replace(" (upcharge applies)", "")
    : slidingScr
      ? slidingScr
      : "";

  const screenColor = useStore((state) => state.quote.screenColor);
  const shadeMeshColor = useStore((state) => state.quote.shadeMeshColor);

  const findRequired = useRequiredMsgs((state) => state.findRequired);
  const removeRequired = useRequiredMsgs((state) => state.removeRequired);

  const displayedImage = useMemo(() => {
    if (!screenVal) return "";

    if (screenVal.includes("Combo")) {
      if (screenLayout === "Left Shade/Right Screen") {
        return "Combo Screen Left";
      }

      if (screenLayout === "Right Shade/Left Screen") {
        return "Combo Screen Right";
      }

      if (screenLayout === "Left Screen/Right Shade") {
        return "Combo Screen Right";
      }

      return "";
    }

    if (screenVal === "Add Double Shade") {
      return "Double Shade";
    }

    if (screenVal === "Add Double Screen") {
      return "Double Screen";
    }

    if (screenVal === "Add Shade") {
      if (screenLayout === "Left Shade") {
        return "Shade Left";
      }

      if (screenLayout === "Right Shade") {
        return "Shade Right";
      }
      return "";
    }

    if (screenLayout) {
      return screenLayout;
    }
  }, [screenVal, screenLayout]);

  const screenOptions = useMemo(
    () => sliderScreenOptions(chosenConfig),
    [chosenConfig],
  );

  useEffect(() => {
    if (!screenTypeChoice) return;

    if (!slidingScreenConfigs.includes(chosenConfig)) {
      return addToQuote({ addSlidingScreen: "" });
    }

    if (screenTypeChoice !== "Sliding Screen") {
      addToQuote({ addSlidingScreen: "" });
    } else {
      if (findRequired("Screen Layout")) removeRequired("Screen Layout");

      addToQuote(
        {
          addScreen: "",
          screenLayout: "",
          screenPocket: "",
        },
        "addScreen",
      );
    }
  }, [screenTypeChoice]);

  const handleAddScreenClick = (label: string) => {
    const checked = label === screenVal;

    if (checked) {
      addToQuote({ addScreen: "" }, "addScreen");
    } else {
      let update: UpdateObj = { addScreen: label };

      if (findRequired("Add Screen")) {
        removeRequired("Add Screen");
      }
      if (findRequired("Centor Screen")) {
        removeRequired("Centor Screen");
      }
      if (findRequired("Brio Screen")) {
        removeRequired("Brio Screen");
      }

      if (label.includes("Double")) {
        if (!isPocketing && chosenConfig.includes("-")) {
          return addToQuote(update);
        }

        if (isPocketing || chosenConfig.includes("Primary")) {
          update["screenPocket"] = "Add Double Pocket";
        } else if (!isPocketing) {
          if (chosenConfig.slice(-1) === "X") {
            update["screenPocket"] = "Add Pocket Right";
          } else {
            update["screenPocket"] = "Add Pocket Left";
          }
        }
      } else {
        //if combo, then there are no default layout values
        const defaultLayout =
          screenOptions && label === "Add Shade"
            ? screenOptions.shadeLayout.filter((item) => item.checkMark)
            : label !== "Add Combo Screen/Shade"
              ? screenOptions?.layout.filter((item) => item.checkMark)
              : [];

        if (defaultLayout?.length) {
          if (findRequired("Screen Layout")) removeRequired("Screen Layout");
          update["screenLayout"] = defaultLayout[0].pdfLabel;
        } else {
          update["screenLayout"] = "";
        }

        if (isPocketing) update["screenPocket"] = "Add Pocket";

        if (!isPocketing && !chosenConfig.includes("Primary"))
          update["screenPocket"] = "";
      }

      addToQuote(update);
    }
  };

  const handleLayoutClick = (
    pdfLabel: string,
    disabled: boolean,
    reqKey: string,
  ) => {
    if (disabled) return;

    const checked = pdfLabel === screenLayout;

    if (checked) {
      addToQuote({ screenLayout: "" });
    } else {
      let update: UpdateObj = { screenLayout: pdfLabel };
      if (findRequired(reqKey)) removeRequired(reqKey);
      if (chosenConfig.includes("Primary")) {
        update["screenPocket"] = "Add Pocket";
      }

      addToQuote(update);
    }
  };

  const handleScreenColorClick = (label: string, reqKey: string) => {
    const checked = label === screenColor;

    if (checked) {
      addToQuote({ screenColor: "" });
    } else {
      addToQuote({ screenColor: label });
      if (findRequired(reqKey)) removeRequired(reqKey);
    }
  };

  const handleShadeColorClick = (label: string, reqKey: string) => {
    const checked = label === shadeMeshColor;

    if (checked) {
      addToQuote({ shadeMeshColor: "" });
    } else {
      addToQuote({ shadeMeshColor: label });
      if (findRequired(reqKey)) removeRequired(reqKey);
    }
  };

  const sliderScrConfigs = slidingScreenConfigs.includes(chosenConfig);

  const showSliding =
    ((chosenConfig === "OX" || chosenConfig === "XO") &&
      sliderOverallWidth <= 150) ||
    ((chosenConfig === "OA-XO" || chosenConfig === "OX-AO") &&
      sliderOverallWidth <= 300)
      ? true
      : false;

  const showSlidingScreen =
    showSliding && screenTypeChoice === "Sliding Screen" ? true : false;

  const hideScreenPocket =
    screenTypeChoice === "Centor Flat Screen/Shade"
      ? true
      : (group6.includes(chosenConfig) || group7.includes(chosenConfig)) &&
          sliderOverallWidth <= 192 &&
          sliderOverallWidth >= 187 &&
          screenVal === "Add Screen"
        ? true
        : false;

  const screenChoices = findScreenChoices(
    isPocketing,
    chosenConfig,
    screenTypeChoice || "",
    screenOptions as SliderScreenOption,
    sliderOverallWidth,
    sliderOverallHeight,
    sillStyle || "",
  );

  useEffect(() => {
    if (!showSliding) {
      return addToQuote({ addSlidingScreen: "" });
    }

    if (screenTypeChoice !== "Sliding Screen") {
      addToQuote({ addSlidingScreen: "" });
    } else {
      if (findRequired("Screen Layout")) removeRequired("Screen Layout");

      addToQuote(
        {
          addScreen: "",
          screenLayout: "",
          screenPocket: "",
        },
        "addScreen",
      );
    }
  }, [screenTypeChoice]);

  useEffect(() => {
    if (!showSliding && screenTypeChoice === "Sliding Screen") {
      addToQuote({ screenType: "", addSlidingScreen: "" });
    }
  }, [showSliding]);

  const chipArrangement2 = {
    marginBottom: 10,
    marginTop: 20,
    alignItems: "center",
    display: "flex",
    flexDirection: "column",
  };

  const helper = {
    borderLeft: `2px solid ${theme.palette.divider}`,
    padding: theme.spacing(1, 2),
  };

  const helper2 = {
    padding: theme.spacing(1, 2),
  };

  const hasShade =
    screenVal === "Add Shade" ||
    screenVal === "Add Double Shade" ||
    screenVal === "Add Combo Screen/Shade"
      ? true
      : false;

  const centorCushion = 0.125; //added on 05/22/2025 per Brian because screen sizes were "too exact"

  let centorWidth = sliderOverallWidth - 1 - centorCushion;

  if (isPocketing) {
    const doubleSided = chosenConfig.includes("-");
    centorWidth = doubleSided
      ? sliderOverallWidth + 8.3128 - centorCushion
      : sliderOverallWidth + 6.8113 - centorCushion;
  }

  let screenTypeOptions: ScreenTypeObj[] = showSliding
    ? screenType
    : screenType.filter((itm) => itm.label !== "Sliding Screen");

  if (centorWidth > 354) {
    screenTypeOptions = screenTypeOptions.filter(
      (itm) => !itm.label.includes("Centor"),
    );
  }

  const showSliderScreens =
    (sliderOverallWidth < 384 && screenTypeChoice && sliderScrConfigs) ||
    (sliderOverallWidth < 384 &&
      !sliderScrConfigs &&
      !screenTypeChoice &&
      screenTypeOptions.length === 1) ||
    (sliderOverallWidth < 384 && !sliderScrConfigs && screenTypeChoice);

  return {
    showSliderScreens,
    isSlimline,
    displayedImage,
    screenOptions,
    handleAddScreenClick,
    handleLayoutClick,
    handleScreenColorClick,
    handleShadeColorClick,
    showSlidingScreen,
    hideScreenPocket,
    screenChoices,
    chipArrangement2,
    helper,
    helper2,
    hasShade,
    chosenConfig,
    screenTypeOptions,
    screenTypeChoice,
    screenVal,
    screenLayout,
    shadeMeshColor,
    screenColor,
    isPocketing,
  };
}

export default useSliderScreenSelector;
